// Tenant identity roster panel. Mounted under TenantLayout at
// /accounts/$a/tenants/$t/identity. Reads for everyone; Add/Edit/Delete
// gated to platform-admins. No PageHeader (TenantLayout already renders
// the tenant header + tabs) — keeps panel tests router-free.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { identityMembers } from "../../api/endpoints/identityMembers";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Modal } from "../../components/ui/Modal";
import { Table } from "../../components/ui/Table";
import { useIsPlatformAdmin } from "../auth/useIsPlatformAdmin";
import type { IdentityMemberOut } from "../../api/types/identityMember";

import { IdentityMemberForm } from "./IdentityMemberForm";

export function IdentityRosterPanel({ tenantSlug }: { tenantSlug: string }) {
  const canWrite = useIsPlatformAdmin();
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<IdentityMemberOut | null>(null);
  const [deleting, setDeleting] = useState<IdentityMemberOut | null>(null);
  const [formPending, setFormPending] = useState(false);

  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.identityMembers(tenantSlug),
    queryFn: () => identityMembers.list(tenantSlug).then((r) => r.data),
  });

  const del = useMutation({
    mutationFn: (id: string) => identityMembers.remove(tenantSlug, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.identityMembers(tenantSlug) });
      setDeleting(null);
    },
  });

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }
  function openEdit(m: IdentityMemberOut) {
    setEditing(m);
    setFormOpen(true);
  }
  function closeForm() {
    setFormOpen(false);
    setEditing(null);
    setFormPending(false);
  }

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
        <strong>Identity roster · {tenantSlug}</strong>
        {canWrite && (
          <button type="button" onClick={openCreate} style={primaryBtn}>
            Add member
          </button>
        )}
      </div>

      {q.isPending && <p style={{ color: "var(--text-dim)" }}>Loading…</p>}
      {q.error && <ErrorBanner error={q.error} title="Failed to list identity members" />}
      {q.data && (
        <Table<IdentityMemberOut>
          columns={[
            { key: "username", header: "Username", render: (m) => <code>{m.username}</code> },
            { key: "name", header: "Name", render: (m) => `${m.first_name} ${m.last_name}` },
            { key: "email", header: "Email", render: (m) => m.email },
            { key: "role", header: "Role", render: (m) => m.role },
            {
              key: "actions",
              header: "",
              render: (m) =>
                canWrite ? (
                  <span style={{ display: "inline-flex", gap: 8 }}>
                    <button type="button" onClick={() => openEdit(m)} style={linkBtn}>Edit</button>
                    <button type="button" onClick={() => setDeleting(m)} style={linkBtn}>Delete</button>
                  </span>
                ) : null,
            },
          ]}
          rows={q.data}
          rowKey={(m) => m.id}
          empty={
            <EmptyState
              title="No identity members yet"
              description="Add the tenant's customer admins/users. At least one tenant_admin is required before an identity onboarding run can render."
              action={
                canWrite ? (
                  <button type="button" onClick={openCreate} style={primaryBtn}>Add member</button>
                ) : undefined
              }
            />
          }
        />
      )}

      <Modal
        open={formOpen}
        onClose={closeForm}
        closeDisabled={formPending}
        title={editing ? `Edit ${editing.username}` : `Add member to ${tenantSlug}`}
      >
        <IdentityMemberForm
          tenantSlug={tenantSlug}
          member={editing ?? undefined}
          onDone={closeForm}
          onPendingChange={setFormPending}
        />
      </Modal>

      <Modal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        closeDisabled={del.isPending}
        title="Delete member"
      >
        <p style={{ marginTop: 0 }}>
          Remove <code>{deleting?.username}</code> from this tenant's roster? This does not
          de-provision any already-created identity account.
        </p>
        {del.error && <ErrorBanner error={del.error} title="Delete failed" />}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button type="button" onClick={() => setDeleting(null)} disabled={del.isPending} style={secondaryBtn}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => deleting && del.mutate(deleting.id)}
            disabled={del.isPending}
            style={dangerBtn}
          >
            {del.isPending ? "…" : "Delete"}
          </button>
        </div>
      </Modal>
    </>
  );
}

const primaryBtn: React.CSSProperties = {
  marginLeft: "auto", background: "var(--accent)", color: "var(--bg)", border: "none",
  padding: "8px 16px", borderRadius: 4, fontSize: "0.9rem", fontWeight: 600, cursor: "pointer",
};
const secondaryBtn: React.CSSProperties = {
  background: "transparent", color: "var(--text-dim)", border: "1px solid var(--border)",
  padding: "8px 16px", borderRadius: 4, fontSize: "0.9rem", cursor: "pointer",
};
const dangerBtn: React.CSSProperties = {
  background: "#7c2d12", color: "#fed7aa", border: "none",
  padding: "8px 16px", borderRadius: 4, fontSize: "0.9rem", fontWeight: 600, cursor: "pointer",
};
const linkBtn: React.CSSProperties = {
  background: "transparent", border: "none", color: "var(--accent)", cursor: "pointer",
  fontSize: "0.85rem", padding: 0,
};
